import { Button, cn } from "@beweegbaas/ui";
import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import type { ComponentProps } from "react";
import { m } from "#/paraglide/messages";
import { UserNavigation } from "#/components/menu.user";
import { useUser } from "#/modules/util.auth/use-user";
import { useLocaleParams } from "#/modules/util.helpers/route-href";

interface LayoutAuthProps extends ComponentProps<"main"> {
  showVisuals?: boolean;
  containerClassName?: string;
}

const visuals = [
  { src: "/stretch-visual.svg", alt: "Beweegbaas stretch visual" },
  { src: "/bowing-backwards-visual.svg", alt: "Beweegbaas bowing backwards visual" },
  { src: "/walking-visual.svg", alt: "Beweegbaas walking visual" },
  { src: "/swinging-visual.svg", alt: "Beweegbaas stretch visual" },
  { src: "/break-dance-visual.svg", alt: "Beweegbaas break dance visual" },
];

export function LayoutAuth({
  children,
  showVisuals = true,
  className,
  containerClassName,
  ...props
}: LayoutAuthProps) {
  const localeLinkParams = useLocaleParams();
  const user = useUser();

  return (
    <main className={cn(`flex min-h-dvh flex-col gap-0 px-4 sm:px-8`, className)} {...props}>
      <div className="flex items-center justify-between gap-6 pt-4 text-sm">
        {/* Logo — light only (no dark/blue variants) */}
        <Link to="/$locale/{-$schoolyear}" params={localeLinkParams}>
          <img src={"/mark-blue.svg"} alt={"Beweeg Baas"} className={"max-w-10 md:hidden"} />
          <img src="/logo-blue.svg" alt="Beweegbaas" className={"hidden md:block max-w-[229px]"} />
        </Link>

        <div className="flex items-center justify-end gap-6">
          {user ? (
            <UserNavigation />
          ) : (
            <>
              <Link
                to="/$locale/{-$schoolyear}/login"
                params={localeLinkParams}
                className="text-muted-foreground hover:text-foreground text-sm transition-colors"
              >
                {m["app.auth.login"]()}
              </Link>

              <Button
                variant={"secondary"}
                nativeButton={false}
                render={
                  <Link to="/$locale/{-$schoolyear}/signup/step/1" params={localeLinkParams} />
                }
              >
                {m["common.actions.registreren"]()}
              </Button>
            </>
          )}
        </div>
      </div>

      <div
        className={cn(
          `mx-auto flex w-full max-w-[500px] grow flex-col items-center justify-center py-4 md:py-10`,
          containerClassName,
        )}
      >
        {children}
      </div>

      {showVisuals ? (
        <div
          className="bottom-0 left-1/2 flex
              w-full justify-between overflow-hidden px-20 pt-10"
        >
          {visuals.map((visual) => (
            <motion.img key={visual.src} src={visual.src} alt={visual.alt} />
          ))}
        </div>
      ) : null}
    </main>
  );
}
